























import { EventBus, SafioEvents } from '@/services/event-bus'
import DtoFactory from '@/util/dto-factory'
import { AccountForecastEdit, PaginationResult, ProductAttributeOptionDto, VendorLocationDto, VendorProductDto,
  DataGroupDataImportRunLogDto } from '@basic-code/shared'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { SafioMessages } from '@/services/dialog-events'
import { SkuAccounts, VendorFilter } from '@/types'

@Component
export default class PaginationNav extends Vue {

  @Prop({ required: true, default: () => { return DtoFactory.placeholderPaginationResult() } })
  public paginationResult: PaginationResult<AccountForecastEdit[]|SkuAccounts[]|ProductAttributeOptionDto[]|
      VendorLocationDto[]|VendorProductDto[]|VendorFilter[]|DataGroupDataImportRunLogDto[]>

  @Prop({ required: true })
  public changeEventName: string

  @Prop({ default: false, type: Boolean })
  public loading: boolean

  @Prop({ type: String, default: 'sku' })
  public sectionType: string

  isDirty = false

  created() {
    EventBus.$on(SafioEvents.forecastChangeDirty, this.updateDirty)
  }

  beforeDestroy() {
    EventBus.$off(SafioEvents.forecastChangeDirty, this.updateDirty)
  }

  get isFirstPage (): boolean {
    if (!this.paginationResult) {
      return true
    }
    return this.paginationResult.currentPage === 1
  }

  get isLastPage (): boolean {
    if (!this.paginationResult || this.paginationResult.totalPages === 0) { return true }
    return this.paginationResult.currentPage === this.paginationResult.totalPages
  }

  get statusDisplay() {
    return `${this.paginationResult.totalPages ? this.paginationResult.currentPage : 0} of ${this.paginationResult.totalPages}`
  }

  get currentPageDisplay(): number {
    return this.totalPagesDisplay === 0 ? 0 : this.paginationResult.currentPage || 1
  }

  get totalPagesDisplay(): number {
    if (!this.paginationResult) {
      return 0
    }
    return this.paginationResult.totalPages || 0
  }

  private updateDirty(isDirty: boolean) {
    this.isDirty = isDirty
  }

  private async canChangePage(): Promise<boolean> {
    if (this.isDirty) {
      const response = await this.$bvModal.msgBoxConfirm(SafioMessages.discardChanges)
      if (!response) {
        return false
      } else {
        this.isDirty = false
      }
    }
    return true
  }

  private getValidatedPage(page: string | number | null): number | null {
    if (page === null || page === undefined || page === '') {
      return null
    }

    const parsedPage = Number(page)
    if (Number.isNaN(parsedPage)) {
      return null
    }

    if (this.totalPagesDisplay === 0) {
      return null
    }

    if (parsedPage < 1) {
      return 1
    }

    if (parsedPage > this.totalPagesDisplay) {
      return this.totalPagesDisplay
    }

    return parsedPage
  }

  private async emitPageChange(pageNumber: number) {
    if (pageNumber < 1 || pageNumber > this.totalPagesDisplay || this.totalPagesDisplay === 0) {
      return
    }

    if (await this.canChangePage()) {
      EventBus.$emit(this.changeEventName, pageNumber, this.paginationResult)
    }
  }

  async onPageInput(event: Event) {
    const input = event.target as HTMLInputElement
    const requestedPage = this.getValidatedPage(input.value)

    if (requestedPage === null) {
      input.value = String(this.currentPageDisplay)
      return
    }

    if (requestedPage === this.currentPageDisplay) {
      input.value = String(requestedPage)
      return
    }

    await this.emitPageChange(requestedPage)
    input.value = String(this.currentPageDisplay)
  }

  async goFirst() {
    await this.emitPageChange(1)
  }

  async goPrev() {
    if (this.paginationResult.currentPage > 1) {
      await this.emitPageChange(this.paginationResult.currentPage - 1)
    }
  }

  async goNext() {
    if (this.paginationResult.currentPage < this.paginationResult.totalPages) {
      await this.emitPageChange(this.paginationResult.currentPage + 1)
    }
  }

  async goLast() {
    if (this.paginationResult.currentPage !== this.paginationResult.totalPages) {
      await this.emitPageChange(this.paginationResult.totalPages)
    }
  }
}
